import { type FC, useMemo, useEffect } from "react";

import { FileText } from "lucide-react";

import { Tag } from "@/shared/components/ui/tag";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { formatContactName } from "@/shared/utils/contactUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { PersonContact } from "@/shared/types/estateContact";

type RevealVariant = "A" | "B" | "C";

export const AdminRevealScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer }) => {
  const { person, user } = useAppContext();
  const decedentName = person?.first_name ?? "the decedent";
  const { contacts } = useUserContacts();

  const { variant, topTierPerson, distributees } = useMemo(() => {
    const distContacts = contacts.filter((c) => c.is_distributee && c.person_status !== "passed_away");
    const canServeContacts = distContacts.filter((c) => c.can_serve);

    const byEmail = contacts.find(
      (c) => c.email && user?.email && c.email.toLowerCase() === user.email.toLowerCase(),
    );
    const firstName = (user?.first_name ?? "").trim().toLowerCase();
    const lastName = (user?.last_name ?? "").trim().toLowerCase();
    const me = (byEmail?.priority_tier != null ? byEmail : null)
      ?? ((firstName || lastName)
        ? contacts.find(
            (c) =>
              c.is_distributee &&
              (c.first_name ?? "").trim().toLowerCase() === firstName &&
              (c.last_name ?? "").trim().toLowerCase() === lastName,
          )
        : null)
      ?? byEmail;

    if (canServeContacts.length === 0) {
      return { variant: "C" as RevealVariant, topTierPerson: null, distributees: distContacts };
    }

    const topTier = Math.min(...canServeContacts.map((c) => c.priority_tier ?? 99));
    const sameTierCanServe = canServeContacts.filter((c) => c.priority_tier === topTier);
    const userIsTopTier = me && me.priority_tier === topTier && me.can_serve;

    let v: RevealVariant;
    let topPerson: PersonContact | null;

    if (userIsTopTier && sameTierCanServe.length === 1) {
      v = "A";
      topPerson = me;
    } else if (userIsTopTier && sameTierCanServe.length > 1) {
      v = "B";
      topPerson = me;
    } else {
      v = "C";
      topPerson = sameTierCanServe[0] ?? null;
    }

    return { variant: v, topTierPerson: topPerson, distributees: distContacts };
  }, [contacts, user?.email, user?.first_name, user?.last_name]);

  const topTier = useMemo(() => {
    const canServe = distributees.filter((c) => c.can_serve);
    if (canServe.length === 0) return 99;
    return Math.min(...canServe.map((c) => c.priority_tier ?? 99));
  }, [distributees]);

  // Persist variant for downstream screens
  useEffect(() => {
    if (answers["admin_confirm_admin_variant"] !== variant) {
      setAnswer("admin_confirm_admin_variant", variant);
    }
    if (topTierPerson && answers["admin_confirm_admin_top_person_id"] !== String(topTierPerson.id)) {
      setAnswer("admin_confirm_admin_top_person_id", String(topTierPerson.id));
    }
  }, [variant, topTierPerson, answers, setAnswer]);

  const topPersonName = topTierPerson ? formatContactName(topTierPerson.first_name, topTierPerson.last_name) : "";
  const topPersonRel = topTierPerson?.relationship ? getRelationshipLabel(topTierPerson.relationship) : "";

  const adminCardSubtitle: Record<RevealVariant, string> = {
    A: "First in line to serve",
    B: "Applying with consent from family who share your place in line",
    C: topPersonRel ? `${topPersonRel} · First in line to serve` : "First in line to serve",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Here's where things stand
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          {variant === "A"
            ? "You're first in line to serve as administrator. Family further down the line gets a formal notice by mail, and nothing else is needed from them."
            : variant === "B"
              ? "You share first place in line to serve as administrator with the family members below. Before the court gives you legal authority, each of them needs to sign a consent or apply with you."
              : topPersonName
                ? `${topPersonName}, ${decedentName}'s ${topPersonRel.toLowerCase()}, is first in line to serve as administrator. ${topPersonName} files the court forms, and the rest of the family gets a formal notice by mail.`
                : `We've identified the family members involved in ${decedentName}'s estate. Review who has the right to serve as administrator.`}
        </p>
      </div>

      {topTierPerson && (
        <div className="flex flex-col gap-2 rounded-lg border border-primary bg-brand-subtle p-4">
          <p className="text-sm font-semibold text-brand">Proposed administrator</p>
          <p className="text-base font-semibold">
            {variant === "A" || variant === "B"
              ? `You (${topPersonName})`
              : topPersonName}
          </p>
          <p className="text-sm text-secondary">{adminCardSubtitle[variant]}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="overflow-hidden rounded-lg border border-primary">
          {distributees.filter((c) => !topTierPerson || c.id !== topTierPerson.id).map((contact, i) => {
            const isMe = contact.email && user?.email && contact.email.toLowerCase() === user.email.toLowerCase();
            const isTopTier = contact.priority_tier === topTier && contact.can_serve;
            const relLabel = contact.relationship ? getRelationshipLabel(contact.relationship) : "";
            const subtitle = isMe
              ? "You"
              : variant === "B" && isTopTier
                ? `${relLabel} · Same place in line as you`
                : variant === "A"
                  ? `${relLabel} · Gets notice by mail`
                  : relLabel;
            const tagLabel = isTopTier ? "Signature needed" : "Notice by mail";
            const tagColor = isTopTier ? "green" : "stone";
            return (
              <div
                key={contact.id}
                className={`flex items-center justify-between bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
              >
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-semibold">{formatContactName(contact.first_name, contact.last_name)}</p>
                  <p className="text-sm text-secondary">{subtitle}</p>
                </div>
                <Tag color={tagColor}>{tagLabel}</Tag>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-primary bg-background p-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
          <FileText className="size-4 text-secondary" />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">Saved to your Contacts</p>
          <p className="text-sm text-secondary">
            We saved everyone from this list to your Contacts page. Gather addresses,
            send invites, and track signatures whenever you are ready.
          </p>
        </div>
      </div>
    </div>
  );
};
